import { Clock10, Loader } from "lucide-react";
import React, { useEffect, useState } from "react";
import { notify, getPractice, can_visit } from "../../../helpers";
import { useDocumentTitle } from "../../../hooks/customDocumentTitleHook";
import Spacing from "../../Spacing";
import { getAppointmentSlotData, updateAppointmentTimeSlot } from "../../../services/appointment";

export default function AppointmentTimeSlot({selectedPracticeChange, fetchChangePracticeData}) {

  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [enableLoader, setEnableLoader] = useState(false);
  const [timeSlotData, setTimeSlotData] = useState([]);
  const [updatedValues, setUpdatedValues] = useState({});

  const fetchAppointMentSlotData = async () => {
    setEnableLoader(true);
    try {
      const response = await getAppointmentSlotData({
        location_id: location_id,
        account_id: account_id,
      });

      if (response) {
        setTimeSlotData(response?.data);
        setEnableLoader(false);
      }
    } catch (error) {
      setEnableLoader(false);
      console.error("Error fetching data:", error);
    }
  };

  const slotTimes = Array.from(
    new Set(
      Object.values(timeSlotData)
        .flat()
        .map((slot) => slot.slot_time)
    )
  );

  const handleSwitchChange = (id, checked) => {
    setUpdatedValues((prev) => ({
      ...prev,
      [id]: !checked,
    }));
  
    setTimeSlotData((prevData) => {
      const updatedData = { ...prevData };
      for (const day in updatedData) {
        updatedData[day] = updatedData[day].map((slot) =>
          slot.id === id ? { ...slot, is_off: !checked } : slot
        );
      }
      return updatedData;
    });
  };

  useEffect(() => {
    fetchAppointMentSlotData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);

  const handleTimeSlotUpdate = async (e) => {
    e.preventDefault();

    if(can_visit("settings_appointment_time_setup")) {
      try {
        if (Object.keys(updatedValues).length === 0) {
          notify("error", "No data change available");
          return;
        }

        setEnableLoader(true);
        e.target.disabled = true;

        const response = await updateAppointmentTimeSlot({
          location_id: location_id,
          account_id: account_id,
          updatedValues: updatedValues,
        });

        if (response.detail) {
          notify('error', response.detail);
        } else { 
          if(response.data.status === 'success'){
            notify("success", response?.data?.message);
            fetchAppointMentSlotData();
          } else {
            notify("error", response?.data?.message);
          }
          setUpdatedValues({});
        }

        setEnableLoader(false);
        e.target.disabled = false;
      } catch (error) {
        setEnableLoader(false);
        // console.log("🚀 ~ handleTimeSlotUpdate ~ error:", error);
      }
    }
  };

  useDocumentTitle("Appointment Time Slot");

  return (
    <>
      <h3 className="docpro_text_center docpro_fs_24 docpro_mb_0">Appointment Time Slot</h3>
      <Spacing lg="22" sm="22" />
      <div className='docpro_card' style={{ marginLeft: '-20px' }}>
        <form onSubmit={handleTimeSlotUpdate} autoComplete="off" className="relative">

          <div className="docpro_table_responsive" style={{ maxHeight: "58vh", overflow: "hidden" }}>
            <table className="docpro_mb_0" style={{  width: "100%" }}>
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                <tr>
                  <th style={{ width: '16%' }}>Time</th>
                  <th className="docpro_text_center" style={{ width: '12%' }}>Monday</th>
                  <th className="docpro_text_center" style={{ width: '12%' }}>Tuesday</th>
                  <th className="docpro_text_center" style={{ width: '12%' }}>Wednesday</th>
                  <th className="docpro_text_center" style={{ width: '12%' }}>Thursday</th>
                  <th className="docpro_text_center" style={{ width: '12%' }}>Friday</th>
                  <th className="docpro_text_center" style={{ width: '12%' }}>Saturday</th>
                  <th className="docpro_text_center" style={{ width: '12%' }}>Sunday</th>
                </tr>
              </thead>
            </table>
            <div className="docpro_table_responsive custom-scroll" style={{ maxHeight: "55vh", overflowY: "auto" }}>
              <table className="docpro_mb_0" style={{ width: "100%" }}>
                <tbody>
                  {!enableLoader ? (
                    slotTimes.length > 0 ? (
                      slotTimes?.map((time, index) => (
                        <tr key={index}>
                          <td style={{ width: '16%' }}>
                            <div className="flex items-center">
                              <Clock10 width={16} height={16}  className="mr-1" /> {time}
                            </div>
                          </td>
                          {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => {
                            const daySlot = timeSlotData[day]?.find((slot) => slot.slot_time === time);
                            return (
                              <td key={day} className="docpro_text_center" style={{ width: '12%' }}>
                                <label className="switch docpro_mb_0 mt-1">
                                  <input
                                    type="checkbox"
                                    name="global_schedule"
                                    defaultChecked={!daySlot?.is_off}
                                    onChange={(e) => handleSwitchChange(daySlot?.id, e.target.checked)}
                                  />
                                  <span className="slider round"></span>
                                </label>
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className="no_data" colSpan="8">
                          No data available
                        </td>
                      </tr>
                    )
                  ) : (
                    <tr>
                      <td className="no_data" colSpan="8">
                        Loading...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Spacing lg="32" sm="32" />
          {(can_visit("settings") && can_visit("settings_appointment_time_setup")) && (
            <div className="docpro_modal_btns">
              <button
                className={`docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg`}
                style={{ pointerEvents: `${enableLoader ? "none" : "initial"}` }}
                type="submit"
              >
                Update
                {enableLoader && (
                  <span className="docpro_loading">
                    <Loader width={20} height={20} />
                  </span>
                )}
              </button>
            </div>
          )}

          {enableLoader && (
            <div className="absolute top-0 left-0 w-full" style={{ height: '100%', zIndex: 3 }}>
              <div className="flex items-center loading" style={{ width: '100%', height: '100%', justifyContent: 'center', background: 'rgb(255 255 255 / 63%)' }}>
                <span className="docpro_loading" style={{ position: 'inherit' }}>
                  <Loader width={46} height={46} style={{ color: 'var(--accent)' }} />
                </span>
              </div>
            </div>
          )}
        </form>
      </div>
    </>
  );
}
